import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { habitsApi } from '../api/client';
import type { HabitDefinition, HabitEntry } from '../api/client';
import './Habits.css';

const Habits: React.FC = () => {
  const navigate = useNavigate();
  const [habitDefinitions, setHabitDefinitions] = useState<HabitDefinition[]>([]);
  const [habitEntries, setHabitEntries] = useState<HabitEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'track' | 'definitions'>('track');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [newDefinition, setNewDefinition] = useState({
    name: '',
    description: '',
    category: 'health' as 'health' | 'productivity' | 'personal' | 'social' | 'learning',
    frequency: 'daily' as 'daily' | 'weekly' | 'monthly' | 'custom',
    targetValue: '',
    unit: ''
  });
  const [newEntry, setNewEntry] = useState({
    habitId: '',
    date: new Date().toISOString().split('T')[0],
    completed: false,
    value: '',
    notes: ''
  });

  useEffect(() => {
    fetchHabitsData();
  }, []);

  const fetchHabitsData = async () => {
    try {
      setLoading(true);
      const [definitionsResponse, entriesResponse] = await Promise.all([
        habitsApi.getHabitDefinitions(),
        habitsApi.getHabitEntries()
      ]);
      
      setHabitDefinitions(definitionsResponse.data);
      setHabitEntries(entriesResponse.data);
    } catch (err) {
      setError('Failed to load habits data');
      console.error('Habits error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDefinition = async () => {
    if (!newDefinition.name.trim()) return;

    try {
      const definitionData = {
        name: newDefinition.name,
        description: newDefinition.description || undefined,
        category: newDefinition.category,
        frequency: newDefinition.frequency,
        targetValue: newDefinition.targetValue ? parseFloat(newDefinition.targetValue) : undefined,
        unit: newDefinition.unit || undefined,
        isActive: true
      };

      await habitsApi.createHabitDefinition(definitionData);
      await fetchHabitsData();
      setShowAddModal(false);
      resetNewDefinition();
    } catch (err) {
      console.error('Error adding habit definition:', err);
      setError('Failed to add habit definition');
    }
  };

  const handleAddEntry = async () => {
    if (!newEntry.habitId) return;

    try {
      const habitDef = habitDefinitions.find(h => h.id === newEntry.habitId);
      const entryData = {
        habitId: newEntry.habitId,
        date: newEntry.date,
        completed: newEntry.completed,
        value: newEntry.value ? parseFloat(newEntry.value) : undefined,
        unit: habitDef?.unit,
        notes: newEntry.notes || undefined
      };

      await habitsApi.createHabitEntry(entryData);
      await fetchHabitsData();
      setShowEntryModal(false);
      resetNewEntry();
    } catch (err) {
      console.error('Error adding habit entry:', err);
      setError('Failed to add habit entry');
    }
  };

  const handleDeleteDefinition = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this habit definition?')) return;

    try {
      await habitsApi.deleteHabitDefinition(id);
      await fetchHabitsData();
    } catch (err) {
      console.error('Error deleting habit definition:', err);
      setError('Failed to delete habit definition');
    }
  };

  const resetNewDefinition = () => {
    setNewDefinition({
      name: '',
      description: '',
      category: 'health',
      frequency: 'daily',
      targetValue: '',
      unit: ''
    });
  };

  const resetNewEntry = () => {
    setNewEntry({
      habitId: '',
      date: new Date().toISOString().split('T')[0],
      completed: false,
      value: '',
      notes: ''
    });
  };

  const handleViewHistory = (habit: HabitDefinition) => {
    navigate(`/habits/${habit.id}`);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'health': return '❤️';
      case 'productivity': return '⚡';
      case 'personal': return '🌱';
      case 'social': return '👥';
      case 'learning': return '📚';
      default: return '✅';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'health': return '#e74c3c';
      case 'productivity': return '#3498db';
      case 'personal': return '#27ae60';
      case 'social': return '#f39c12';
      case 'learning': return '#9b59b6';
      default: return '#95a5a6';
    }
  };

  const getHabitProgress = (habitId: string, targetDate: string) => {
    const entry = habitEntries.find(e => e.habitId === habitId && e.date === targetDate);
    const habit = habitDefinitions.find(h => h.id === habitId);
    
    if (!entry) return { completed: false, progress: 0, entry: null };
    
    let progress = 0;
    if (habit?.targetValue && entry.value) {
      progress = Math.min((entry.value / habit.targetValue) * 100, 100);
    } else if (entry.completed) {
      progress = 100;
    }
    
    return { completed: entry.completed, progress, entry };
  };

  const getStreakCount = (habitId: string) => {
    const entries = habitEntries
      .filter(e => e.habitId === habitId && e.completed)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    
    if (entries.length === 0) return 0;
    
    let streak = 0;
    let currentDate = new Date();
    
    for (const entry of entries) {
      const entryDate = new Date(entry.date);
      const diffDays = Math.floor((currentDate.getTime() - entryDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (diffDays === streak) {
        streak++;
        currentDate = entryDate;
      } else {
        break;
      }
    }
    
    return streak;
  };

  if (loading) {
    return <div className="habits-page"><div className="loading">Loading habits...</div></div>;
  }

  if (error) {
    return <div className="habits-page"><div className="error">Error: {error}</div></div>;
  }

  const activeHabits = habitDefinitions.filter(h => h.isActive);

  return (
    <div className="habits-page">
      <header className="page-header">
        <h1>✅ Habits Tracker</h1>
        <div className="header-actions">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="date-input"
          />
          <button 
            className="btn btn-primary" 
            onClick={() => setShowEntryModal(true)}
            disabled={activeHabits.length === 0}
          >
            Log Habit
          </button>
        </div>
      </header>

      {error && <div className="error-message">{error}</div>}

      {/* Tab Navigation */}
      <div className="habits-tabs">
        <button 
          className={`tab-button ${activeTab === 'track' ? 'active' : ''}`}
          onClick={() => setActiveTab('track')}
        >
          📊 Track Habits ({activeHabits.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'definitions' ? 'active' : ''}`}
          onClick={() => setActiveTab('definitions')}
        >
          ⚙️ Manage Definitions ({habitDefinitions.length})
        </button>
      </div>

      {/* Tab Content */}
      <div className="habits-content">
        {activeTab === 'track' && (
          <div className="tracking-section">
            <div className="tracking-header">
              <h2>Habits for {new Date(selectedDate).toLocaleDateString()}</h2>
            </div>
            
            <div className="habits-grid">
              {activeHabits.map((habit) => {
                const progress = getHabitProgress(habit.id, selectedDate);
                const streak = getStreakCount(habit.id);
                
                return (
                  <div
                    key={habit.id}
                    className="habit-card clickable"
                    onClick={() => handleViewHistory(habit)}
                  >
                    <div className="habit-header">
                      <div className="habit-title">
                        <span className="habit-icon">{getCategoryIcon(habit.category)}</span>
                        <div className="habit-info">
                          <h3 className="habit-name">{habit.name}</h3>
                          <span
                            className="habit-category"
                            style={{ backgroundColor: getCategoryColor(habit.category) }}
                          >
                            {habit.category}
                          </span>
                        </div>
                      </div>
                      <div className="habit-status">
                        {progress.completed ? (
                          <span className="status-completed">✅</span>
                        ) : (
                          <span className="status-pending">⏳</span>
                        )}
                      </div>
                    </div>

                    <div className="habit-content">
                      {habit.description && (
                        <p className="habit-description">{habit.description}</p>
                      )}

                      <div className="habit-details">
                        <div className="detail-row">
                          <span className="detail-label">Frequency:</span>
                          <span className="detail-value">{habit.frequency}</span>
                        </div>
                        
                        {habit.targetValue && (
                          <div className="detail-row">
                            <span className="detail-label">Target:</span>
                            <span className="detail-value">
                              {habit.targetValue} {habit.unit}
                            </span>
                          </div>
                        )}

                        <div className="detail-row">
                          <span className="detail-label">Current Streak:</span>
                          <span className="detail-value streak">🔥 {streak} days</span>
                        </div>
                      </div>

                      {habit.targetValue && progress.entry && (
                        <div className="progress-section">
                          <div className="progress-header">
                            <span className="progress-label">Progress</span>
                            <span className="progress-value">
                              {progress.entry.value || 0} / {habit.targetValue} {habit.unit}
                            </span>
                          </div>
                          <div className="progress-bar">
                            <div 
                              className="progress-fill"
                              style={{ width: `${progress.progress}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {progress.entry?.notes && (
                        <div className="entry-notes">
                          <span className="notes-icon">📝</span>
                          <p className="notes-text">{progress.entry.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {activeHabits.length === 0 && (
                <div className="no-habits">
                  <span className="no-habits-icon">✅</span>
                  <p>No active habits. Create some habit definitions to get started!</p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => setActiveTab('definitions')}
                  >
                    Manage Definitions
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'definitions' && (
          <div className="definitions-section">
            <div className="section-header">
              <h2>Habit Definitions</h2>
              <button 
                className="btn btn-primary" 
                onClick={() => setShowAddModal(true)}
              >
                Add New Habit
              </button>
            </div>
            
            <div className="definitions-grid">
              {habitDefinitions.map((habit) => (
                <div key={habit.id} className="definition-card">
                  <div className="definition-header">
                    <div className="definition-title">
                      <span className="habit-icon">{getCategoryIcon(habit.category)}</span>
                      <h3 className="definition-name">{habit.name}</h3>
                    </div>
                    <div className="definition-actions">
                      <span className={`status-badge ${habit.isActive ? 'active' : 'inactive'}`}>
                        {habit.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <button 
                        className="delete-btn"
                        onClick={() => handleDeleteDefinition(habit.id)}
                        title="Delete habit definition"
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  <div className="definition-content">
                    {habit.description && (
                      <p className="definition-description">{habit.description}</p>
                    )}

                    <div className="definition-details">
                      <div className="detail-row">
                        <span className="detail-label">Category:</span>
                        <span 
                          className="category-badge"
                          style={{ backgroundColor: getCategoryColor(habit.category) }}
                        >
                          {habit.category}
                        </span>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">Frequency:</span>
                        <span className="detail-value">{habit.frequency}</span>
                      </div>

                      {habit.targetValue && (
                        <div className="detail-row">
                          <span className="detail-label">Target:</span>
                          <span className="detail-value">
                            {habit.targetValue} {habit.unit}
                          </span>
                        </div>
                      )}

                      <div className="detail-row">
                        <span className="detail-label">Created:</span>
                        <span className="detail-value">
                          {new Date(habit.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {habitDefinitions.length === 0 && (
                <div className="no-definitions">
                  <span className="no-definitions-icon">⚙️</span>
                  <p>No habit definitions created yet. Create your first habit to start tracking!</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Definition Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Habit Definition</h3>
              <button 
                className="modal-close"
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Habit Name *</label>
                <input
                  type="text"
                  value={newDefinition.name}
                  onChange={(e) => setNewDefinition({...newDefinition, name: e.target.value})}
                  className="form-input"
                  placeholder="e.g., Daily Exercise, Read Technical Articles..."
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newDefinition.description}
                  onChange={(e) => setNewDefinition({...newDefinition, description: e.target.value})}
                  className="form-textarea"
                  rows={2}
                  placeholder="Describe this habit..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <select 
                    value={newDefinition.category}
                    onChange={(e) => setNewDefinition({...newDefinition, category: e.target.value as any})}
                    className="form-select"
                  >
                    <option value="health">Health</option>
                    <option value="productivity">Productivity</option>
                    <option value="personal">Personal</option>
                    <option value="social">Social</option>
                    <option value="learning">Learning</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Frequency</label>
                  <select 
                    value={newDefinition.frequency}
                    onChange={(e) => setNewDefinition({...newDefinition, frequency: e.target.value as any})}
                    className="form-select"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Target Value</label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={newDefinition.targetValue}
                    onChange={(e) => setNewDefinition({...newDefinition, targetValue: e.target.value})}
                    className="form-input"
                    placeholder="e.g., 30, 8, 10000..."
                  />
                </div>

                <div className="form-group">
                  <label>Unit</label>
                  <input
                    type="text"
                    value={newDefinition.unit}
                    onChange={(e) => setNewDefinition({...newDefinition, unit: e.target.value})}
                    className="form-input"
                    placeholder="e.g., minutes, hours, steps..."
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-success"
                onClick={handleAddDefinition}
                disabled={!newDefinition.name.trim()}
              >
                Add Habit
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Entry Modal */}
      {showEntryModal && (
        <div className="modal-overlay" onClick={() => setShowEntryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Log Habit Entry</h3>
              <button
                className="modal-close"
                onClick={() => setShowEntryModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Habit *</label>
                <select
                  value={newEntry.habitId}
                  onChange={(e) => setNewEntry({...newEntry, habitId: e.target.value})}
                  className="form-select"
                >
                  <option value="">Select a habit...</option>
                  {activeHabits.map(habit => (
                    <option key={habit.id} value={habit.id}>
                      {habit.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={newEntry.date}
                  onChange={(e) => setNewEntry({...newEntry, date: e.target.value})}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newEntry.completed}
                    onChange={(e) => setNewEntry({...newEntry, completed: e.target.checked})}
                    className="form-checkbox"
                  />
                  Completed
                </label>
              </div>

              {newEntry.habitId && habitDefinitions.find(h => h.id === newEntry.habitId)?.targetValue && (
                <div className="form-group">
                  <label>
                    Value ({habitDefinitions.find(h => h.id === newEntry.habitId)?.unit})
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={newEntry.value}
                    onChange={(e) => setNewEntry({...newEntry, value: e.target.value})}
                    className="form-input"
                    placeholder="Enter value..."
                  />
                </div>
              )}

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newEntry.notes}
                  onChange={(e) => setNewEntry({...newEntry, notes: e.target.value})}
                  className="form-textarea"
                  rows={3}
                  placeholder="Optional notes about this entry..."
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-success"
                onClick={handleAddEntry}
                disabled={!newEntry.habitId}
              >
                Log Entry
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowEntryModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Habits;